import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import PageShell from '@/components/layout/PageShell';
import EmptyState from '@/components/EmptyState';
import { statusColors, priorityColors, attorneyName } from '@/lib/caseFormatting';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Edit, Trash2, Check, Calendar, Clock,
  DollarSign, FileText, Gavel, CheckCircle2, AlertCircle,
  Activity, RefreshCw, User, Briefcase, AlertTriangle, Paperclip
} from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import CaseAttachments from '@/components/CaseAttachments';


const CaseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  const [caseData, setCaseData] = useState(null);
  const [dockets, setDockets] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [billingSummary, setBillingSummary] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [attorneys, setAttorneys] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  // Docket Dialog
  const [showDocketDialog, setShowDocketDialog] = useState(false);
  const [editingDocket, setEditingDocket] = useState(null);
  const [docketForm, setDocketForm] = useState({
    docket_type: 'activity',
    title: '',
    description: '',
    date: '',
    duration_hours: '',
    reminder: false,
    reminder_date: '',
    status: 'pending'
  });

  // Time Entry Dialog
  const [showTimeDialog, setShowTimeDialog] = useState(false);
  const [editingTimeEntry, setEditingTimeEntry] = useState(null);
  const [timeForm, setTimeForm] = useState({
    docket_id: '',
    description: '',
    billing_type: 'hourly',
    rate: '',
    hours_worked: '',
    cap_amount: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [caseRes, docketsRes, timeRes, billingRes, attorneysRes, timelineRes] = await Promise.all([
        api.get(`/cases/${id}`),
        api.get(`/dockets?case_id=${id}`),
        api.get(`/time-entries?case_id=${id}`),
        api.get(`/cases/${id}/billing-summary`),
        api.get(`/attorneys`),
        api.get(`/cases/${id}/timeline`)
      ]);
      setCaseData(caseRes.data);
      setDockets(docketsRes.data);
      setTimeEntries(timeRes.data);
      setBillingSummary(billingRes.data);
      setAttorneys(attorneysRes.data);
      setTimeline(timelineRes.data);
    } catch (error) {
      console.error('Failed to fetch data', error);
      toast.error('Failed to load case details');
      navigate('/cases');
    } finally {
      setLoadingData(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    } else if (user) {
      if (!['admin', 'senior_partner', 'associate'].includes(user.role)) {
        navigate('/');
        return;
      }
      fetchData();
    }
  }, [user, loading, id, fetchData, navigate]);

  // Docket Functions
  const saveDocket = async () => {
    try {
      const payload = {
        id: editingDocket?.id || uuidv4(),
        case_id: id,
        ...docketForm,
        date: docketForm.date ? new Date(docketForm.date).toISOString() : null,
        reminder_date: docketForm.reminder_date ? new Date(docketForm.reminder_date).toISOString() : null,
        duration_hours: docketForm.duration_hours ? parseFloat(docketForm.duration_hours) : null,
        created_by: editingDocket?.created_by || user.id,
        created_at: editingDocket?.created_at || new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      if (editingDocket) {
        await api.put(`/dockets/${payload.id}`, payload);
        toast.success('Docket updated');
      } else {
        await api.post(`/dockets`, payload);
        toast.success('Docket created');
      }

      setShowDocketDialog(false);
      setEditingDocket(null);
      resetDocketForm();
      fetchData();
    } catch (error) {
      console.error('Failed to save docket', error);
      toast.error('Failed to save docket');
    }
  };

  const deleteDocket = async (docketId) => {
    if (!confirm('Delete this docket? Related time entries will also be deleted.')) return;
    try {
      await api.delete(`/dockets/${docketId}`);
      toast.success('Docket deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete docket');
    }
  };

  const toggleDocketStatus = async (docket) => {
    const newStatus = docket.status === 'completed' ? 'pending' : 'completed';
    try {
      await api.put(`/dockets/${docket.id}`, {
        ...docket,
        status: newStatus,
        updated_at: new Date().toISOString()
      });
      toast.success(`Docket marked as ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update docket');
    }
  };

  const resetDocketForm = () => {
    setDocketForm({
      docket_type: 'activity',
      title: '',
      description: '',
      date: '',
      duration_hours: '',
      reminder: false,
      reminder_date: '',
      status: 'pending'
    });
  };

  // Time Entry Functions
  const saveTimeEntry = async () => {
    try {
      const payload = {
        id: editingTimeEntry?.id || uuidv4(),
        case_id: id,
        docket_id: timeForm.docket_id === 'none' ? null : (timeForm.docket_id || null),
        description: timeForm.description,
        billing_type: timeForm.billing_type,
        rate: parseFloat(timeForm.rate),
        hours_worked: timeForm.hours_worked ? parseFloat(timeForm.hours_worked) : null,
        cap_amount: timeForm.cap_amount ? parseFloat(timeForm.cap_amount) : null,
        billable_amount: 0, // Calculated on backend
        status: editingTimeEntry?.status || 'draft',
        recorded_by: editingTimeEntry?.recorded_by || user.id,
        created_at: editingTimeEntry?.created_at || new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      if (editingTimeEntry) {
        await api.put(`/time-entries/${payload.id}`, payload);
        toast.success('Time entry updated');
      } else {
        await api.post(`/time-entries`, payload);
        toast.success('Time entry created');
      }

      setShowTimeDialog(false);
      setEditingTimeEntry(null);
      resetTimeForm();
      fetchData();
    } catch (error) {
      console.error('Failed to save time entry', error);
      toast.error('Failed to save time entry');
    }
  };

  const deleteTimeEntry = async (entryId) => {
    if (!confirm('Delete this time entry?')) return;
    try {
      await api.delete(`/time-entries/${entryId}`);
      toast.success('Time entry deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete time entry');
    }
  };

  const approveTimeEntry = async (entryId) => {
    try {
      await api.put(`/time-entries/${entryId}/approve`, {});
      toast.success('Time entry approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve time entry');
    }
  };

  const resetTimeForm = () => {
    setTimeForm({
      docket_id: '',
      description: '',
      billing_type: 'hourly',
      rate: '',
      hours_worked: '',
      cap_amount: ''
    });
  };

  // Create time entry from docket
  const createTimeFromDocket = (docket) => {
    setTimeForm({
      docket_id: docket.id,
      description: docket.title,
      billing_type: 'hourly',
      rate: '',
      hours_worked: docket.duration_hours?.toString() || '',
      cap_amount: ''
    });
    setShowTimeDialog(true);
  };

  if (loading || loadingData) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!caseData) {
    return <div className="min-h-screen flex items-center justify-center">Case not found</div>;
  }

  return (
    <PageShell>
      {/* Header */}
      <div className="mb-8">
        <Button asChild variant="ghost" data-testid="back-to-cases">
          <Link to="/cases">
            <ArrowLeft size={16} /> Back to Cases
          </Link>
        </Button>
      </div>

        {/* Case Header Card */}
        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="font-mono text-lg text-accent font-semibold" data-testid="case-number">
                    {caseData.case_number}
                  </span>
                  <Badge className={statusColors[caseData.status]}>
                    {caseData.status.replace('_', ' ')}
                  </Badge>
                  <Badge className={priorityColors[caseData.priority]}>
                    {caseData.priority}
                  </Badge>
                </div>
                <h1 className="font-serif text-3xl font-bold text-primary mb-4" data-testid="case-title">
                  {caseData.title}
                </h1>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Client</p>
                    <p className="font-medium">{caseData.client}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Category</p>
                    <p className="font-medium capitalize">{caseData.category.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Assignee</p>
                    <p className="font-medium">{attorneyName(attorneys, caseData.assignee, 'Unknown')}</p>
                  </div>
                  {caseData.due_date && (
                    <div>
                      <p className="text-muted-foreground">Due Date</p>
                      <p className="font-medium">{new Date(caseData.due_date).toLocaleDateString()}</p>
                    </div>
                  )}
                </div>
                {caseData.summary && (
                  <div className="mt-4 p-4 bg-secondary rounded-lg">
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">{caseData.summary}</p>
                  </div>
                )}
              </div>
              {/* Billing Summary */}
              <div className="bg-primary/5 p-4 rounded-lg min-w-[200px]">
                <h3 className="font-sans text-sm font-medium text-muted-foreground mb-3">Billing Summary</h3>
                <p className="font-serif text-3xl font-bold text-primary">
                  ${billingSummary?.total_billable?.toLocaleString() || '0'}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {billingSummary?.total_hours?.toFixed(1) || 0} hours logged
                </p>
                <div className="mt-3 space-y-1 text-xs">
                  <p><span className="text-muted-foreground">Draft:</span> ${billingSummary?.draft_amount?.toLocaleString() || 0}</p>
                  <p><span className="text-muted-foreground">Approved:</span> ${billingSummary?.approved_amount?.toLocaleString() || 0}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="timeline" className="space-y-6">
          <TabsList>
            <TabsTrigger value="timeline" data-testid="tab-timeline">
              <Activity size={16} className="mr-2" /> Timeline ({timeline.length})
            </TabsTrigger>
            <TabsTrigger value="dockets" data-testid="tab-dockets">
              <FileText size={16} className="mr-2" /> Dockets ({dockets.length})
            </TabsTrigger>
            <TabsTrigger value="time" data-testid="tab-time">
              <Clock size={16} className="mr-2" /> Time Entries ({timeEntries.length})
            </TabsTrigger>
            <TabsTrigger value="attachments" data-testid="tab-attachments">
              <Paperclip size={16} className="mr-2" /> Attachments
            </TabsTrigger>
          </TabsList>

          {/* Timeline Tab */}
          <TabsContent value="timeline">
            <div className="mb-4">
              <p className="text-muted-foreground">Complete activity history for this case</p>
            </div>

            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border" />
              
              <div className="space-y-4">
                {timeline.length === 0 ? (
                  <EmptyState icon={Activity} message="No activity yet." />
                ) : (
                  timeline.map((item, index) => (
                    <div key={item.id} className="relative flex gap-4 pl-2">
                      {/* Icon */}
                      <div className={`relative z-10 flex items-center justify-center w-8 h-8 rounded-full 
                        ${item.type === 'case_created' ? 'bg-primary text-white' : ''}
                        ${item.type === 'docket_created' ? 'bg-blue-100 text-blue-600' : ''}
                        ${item.type === 'docket_completed' ? 'bg-green-100 text-green-600' : ''}
                        ${item.type === 'time_entry_created' ? 'bg-amber-100 text-amber-600' : ''}
                        ${item.type === 'time_entry_approved' ? 'bg-emerald-100 text-emerald-600' : ''}
                        ${item.type === 'status_change' ? 'bg-purple-100 text-purple-600' : ''}
                        ${item.type === 'priority_change' ? 'bg-orange-100 text-orange-600' : ''}
                        ${item.type === 'assignee_change' ? 'bg-cyan-100 text-cyan-600' : ''}
                        ${!['case_created', 'docket_created', 'docket_completed', 'time_entry_created', 'time_entry_approved', 'status_change', 'priority_change', 'assignee_change'].includes(item.type) ? 'bg-gray-100 text-gray-600' : ''}
                      `}>
                        {item.icon === 'briefcase' && <Briefcase size={14} />}
                        {item.icon === 'file-text' && <FileText size={14} />}
                        {item.icon === 'gavel' && <Gavel size={14} />}
                        {item.icon === 'check-circle' && <CheckCircle2 size={14} />}
                        {item.icon === 'check-circle-2' && <CheckCircle2 size={14} />}
                        {item.icon === 'clock' && <Clock size={14} />}
                        {item.icon === 'refresh-cw' && <RefreshCw size={14} />}
                        {item.icon === 'alert-triangle' && <AlertTriangle size={14} />}
                        {item.icon === 'user' && <User size={14} />}
                        {item.icon === 'activity' && <Activity size={14} />}
                      </div>
                      
                      {/* Content */}
                      <Card className="flex-1 hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium text-primary">{item.title}</span>
                                {item.metadata?.billable_amount && (
                                  <Badge variant="outline" className="text-xs">
                                    ${item.metadata.billable_amount.toLocaleString()}
                                  </Badge>
                                )}
                                {item.metadata?.status && (
                                  <Badge variant="outline" className="text-xs capitalize">
                                    {item.metadata.status}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground">{item.description}</p>
                              {item.metadata?.hours && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  {item.metadata.hours} hours • {item.metadata.billing_type?.replace('_', ' ')}
                                </p>
                              )}
                            </div>
                            <div className="text-right text-xs text-muted-foreground whitespace-nowrap">
                              <p>{new Date(item.timestamp).toLocaleDateString()}</p>
                              <p>{new Date(item.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                              <p className="mt-1 text-accent">{item.user}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  ))
                )}
              </div>
            </div>
          </TabsContent>

          {/* Dockets Tab */}
          <TabsContent value="dockets">
            <div className="flex justify-between items-center mb-4">
              <p className="text-muted-foreground">Tasks and court events for this case</p>
              <Button
                onClick={() => {
                  setEditingDocket(null);
                  resetDocketForm();
                  setShowDocketDialog(true);
                }}
                className="bg-primary text-primary-foreground"
                data-testid="add-docket-btn"
              >
                <Plus size={16} className="mr-2" /> Add Docket
              </Button>
            </div>

            <div className="space-y-3">
              {dockets.length === 0 ? (
                <EmptyState icon={FileText} message="No dockets yet. Add one to track tasks and events." />
              ) : (
                dockets.map((docket) => (
                  <Card key={docket.id} className={docket.status === 'completed' ? 'opacity-60' : ''}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 flex-1">
                          <button
                            onClick={() => toggleDocketStatus(docket)}
                            aria-pressed={docket.status === 'completed'}
                            aria-label={
                              docket.status === 'completed'
                                ? `Mark "${docket.title}" incomplete`
                                : `Mark "${docket.title}" complete`
                            }
                            className={`mt-1 p-1 rounded-full border-2 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ${
                              docket.status === 'completed'
                                ? 'bg-green-500 border-green-500 text-white'
                                : 'border-gray-300 hover:border-primary'
                            }`}
                          >
                            {docket.status === 'completed' && <Check size={14} />}
                          </button>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              {docket.docket_type === 'court_event' ? (
                                <Gavel size={16} className="text-accent" />
                              ) : (
                                <FileText size={16} className="text-muted-foreground" />
                              )}
                              <Badge variant="outline" className="text-xs">
                                {docket.docket_type === 'court_event' ? 'Court Event' : 'Activity'}
                              </Badge>
                              {docket.reminder && (
                                <Badge variant="outline" className="text-xs bg-yellow-50">
                                  <AlertCircle size={12} className="mr-1" /> Reminder Set
                                </Badge>
                              )}
                            </div>
                            <h4 className={`font-medium ${docket.status === 'completed' ? 'line-through' : ''}`}>
                              {docket.title}
                            </h4>
                            {docket.description && (
                              <p className="text-sm text-muted-foreground mt-1">{docket.description}</p>
                            )}
                            <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                              {docket.date && (
                                <span className="flex items-center gap-1">
                                  <Calendar size={12} /> {new Date(docket.date).toLocaleString()}
                                </span>
                              )}
                              {docket.duration_hours && (
                                <span className="flex items-center gap-1">
                                  <Clock size={12} /> {docket.duration_hours}h
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => createTimeFromDocket(docket)}
                            aria-label={`Create time entry from "${docket.title}"`}
                          >
                            <DollarSign size={14} />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            aria-label={`Edit docket ${docket.title}`}
                            onClick={() => {
                              setEditingDocket(docket);
                              setDocketForm({
                                docket_type: docket.docket_type,
                                title: docket.title,
                                description: docket.description || '',
                                date: docket.date ? new Date(docket.date).toISOString().slice(0, 16) : '',
                                duration_hours: docket.duration_hours?.toString() || '',
                                reminder: docket.reminder,
                                reminder_date: docket.reminder_date ? new Date(docket.reminder_date).toISOString().slice(0, 16) : '',
                                status: docket.status
                              });
                              setShowDocketDialog(true);
                            }}
                          >
                            <Edit size={14} />
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => deleteDocket(docket.id)}
                            aria-label={`Delete docket ${docket.title}`}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Time Entries Tab */}
          <TabsContent value="time">
            <div className="flex justify-between items-center mb-4">
              <p className="text-muted-foreground">Billable time entries for this case</p>
              <Button
                onClick={() => {
                  setEditingTimeEntry(null);
                  resetTimeForm();
                  setShowTimeDialog(true);
                }}
                className="bg-primary text-primary-foreground"
                data-testid="add-time-btn"
              >
                <Plus size={16} className="mr-2" /> Add Time Entry
              </Button>
            </div>

            <div className="space-y-3">
              {timeEntries.length === 0 ? (
                <EmptyState icon={Clock} message="No time entries yet." />
              ) : (
                timeEntries.map((entry) => (
                  <Card key={entry.id}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge
                              className={
                                entry.status === 'approved'
                                  ? 'bg-green-100 text-green-800'
                                  : entry.status === 'invoiced'
                                  ? 'bg-purple-100 text-purple-800'
                                  : 'bg-gray-100 text-gray-800'
                              }
                            >
                              {entry.status}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {entry.billing_type.replace('_', ' ')}
                            </Badge>
                          </div>
                          <p className="font-medium">{entry.description}</p>
                          <div className="flex gap-4 mt-2 text-sm text-muted-foreground">
                            <span>Rate: ${entry.rate}</span>
                            {entry.hours_worked && <span>Hours: {entry.hours_worked}</span>}
                            {entry.cap_amount && <span>Cap: ${entry.cap_amount}</span>}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-serif text-2xl font-bold text-primary">
                            ${entry.billable_amount?.toLocaleString()}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(entry.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          {entry.status === 'draft' && user.role !== 'associate' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => approveTimeEntry(entry.id)}
                              className="text-green-600 hover:text-green-700"
                              aria-label="Approve time entry"
                            >
                              <CheckCircle2 size={14} />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            aria-label="Edit time entry"
                            onClick={() => {
                              setEditingTimeEntry(entry);
                              setTimeForm({
                                docket_id: entry.docket_id || '',
                                description: entry.description,
                                billing_type: entry.billing_type,
                                rate: entry.rate.toString(),
                                hours_worked: entry.hours_worked?.toString() || '',
                                cap_amount: entry.cap_amount?.toString() || ''
                              });
                              setShowTimeDialog(true);
                            }}
                          >
                            <Edit size={14} />
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => deleteTimeEntry(entry.id)}
                            aria-label="Delete time entry"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Attachments Tab */}
          <TabsContent value="attachments">
            <CaseAttachments caseId={id} />
          </TabsContent>
        </Tabs>

      {/* Docket Dialog */}
      <Dialog open={showDocketDialog} onOpenChange={setShowDocketDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl font-bold text-primary">
              {editingDocket ? 'Edit Docket' : 'New Docket'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Type *</Label>
              <Select
                value={docketForm.docket_type}
                onValueChange={(v) => setDocketForm({ ...docketForm, docket_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="activity">Activity (Research, Drafting, etc.)</SelectItem>
                  <SelectItem value="court_event">Court Event (Hearing, Filing, etc.)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Title *</Label>
              <Input
                value={docketForm.title}
                onChange={(e) => setDocketForm({ ...docketForm, title: e.target.value })}
                placeholder="Docket title"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={docketForm.description}
                onChange={(e) => setDocketForm({ ...docketForm, description: e.target.value })}
                placeholder="Details..."
                rows={3}
              />
            </div>
            {docketForm.docket_type === 'court_event' && (
              <>
                <div>
                  <Label>Date & Time</Label>
                  <Input
                    type="datetime-local"
                    value={docketForm.date}
                    onChange={(e) => setDocketForm({ ...docketForm, date: e.target.value })}
                  />
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={docketForm.reminder}
                      onChange={(e) => setDocketForm({ ...docketForm, reminder: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Set Reminder</span>
                  </label>
                  {docketForm.reminder && (
                    <Input
                      type="datetime-local"
                      value={docketForm.reminder_date}
                      onChange={(e) => setDocketForm({ ...docketForm, reminder_date: e.target.value })}
                      className="flex-1"
                    />
                  )}
                </div>
              </>
            )}
            {docketForm.docket_type === 'activity' && (
              <div>
                <Label>Duration (hours)</Label>
                <Input
                  type="number"
                  step="0.25"
                  value={docketForm.duration_hours}
                  onChange={(e) => setDocketForm({ ...docketForm, duration_hours: e.target.value })}
                  placeholder="e.g., 2.5"
                />
              </div>
            )}
            <div className="flex gap-4 pt-4">
              <Button variant="outline" onClick={() => setShowDocketDialog(false)} className="flex-1">
                Cancel
              </Button>
              <Button
                onClick={saveDocket}
                className="flex-1 bg-primary text-primary-foreground"
                disabled={!docketForm.title}
              >
                {editingDocket ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Time Entry Dialog */}
      <Dialog open={showTimeDialog} onOpenChange={setShowTimeDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl font-bold text-primary">
              {editingTimeEntry ? 'Edit Time Entry' : 'New Time Entry'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Link to Docket (optional)</Label>
              <Select
                value={timeForm.docket_id}
                onValueChange={(v) => setTimeForm({ ...timeForm, docket_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select docket..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No link</SelectItem>
                  {dockets.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description *</Label>
              <Textarea
                value={timeForm.description}
                onChange={(e) => setTimeForm({ ...timeForm, description: e.target.value })}
                placeholder="Work performed..."
                rows={3}
              />
            </div>
            <div>
              <Label>Billing Type *</Label>
              <Select
                value={timeForm.billing_type}
                onValueChange={(v) => setTimeForm({ ...timeForm, billing_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="flat_rate">Flat Rate</SelectItem>
                  <SelectItem value="hourly">Hourly</SelectItem>
                  <SelectItem value="capped_hourly">Capped Hourly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{timeForm.billing_type === 'flat_rate' ? 'Flat Fee ($)' : 'Hourly Rate ($)'} *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={timeForm.rate}
                  onChange={(e) => setTimeForm({ ...timeForm, rate: e.target.value })}
                  placeholder="e.g., 300"
                />
              </div>
              {timeForm.billing_type !== 'flat_rate' && (
                <div>
                  <Label>Hours Worked *</Label>
                  <Input
                    type="number"
                    step="0.25"
                    value={timeForm.hours_worked}
                    onChange={(e) => setTimeForm({ ...timeForm, hours_worked: e.target.value })}
                    placeholder="e.g., 2.5"
                  />
                </div>
              )}
            </div>
            {timeForm.billing_type === 'capped_hourly' && (
              <div>
                <Label>Cap Amount ($) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={timeForm.cap_amount}
                  onChange={(e) => setTimeForm({ ...timeForm, cap_amount: e.target.value })}
                  placeholder="Maximum billable amount"
                />
              </div>
            )}
            <div className="flex gap-4 pt-4">
              <Button variant="outline" onClick={() => setShowTimeDialog(false)} className="flex-1">
                Cancel
              </Button>
              <Button
                onClick={saveTimeEntry}
                className="flex-1 bg-primary text-primary-foreground"
                disabled={
                  !timeForm.description ||
                  !timeForm.rate ||
                  (timeForm.billing_type !== 'flat_rate' && !timeForm.hours_worked) ||
                  (timeForm.billing_type === 'capped_hourly' && !timeForm.cap_amount)
                }
              >
                {editingTimeEntry ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};

export default CaseDetail;
